import { Grid, Typography } from '@material-ui/core'
import React from 'react'
import styled from 'styled-components'

import { CartFragment } from '../../../graphql/magento'
import useSiteMetadata from '../../../hooks/useSiteMetadata'
import {
  collectItemAndDesignFeePairs,
  totalItemsDisplay,
  totalQuantityExcludingDependents,
} from '../../../lib/cart/item'
import Link from '../../global/Link'
import CaliforniaProp65Warning from './CaliforniaProp65Warning'
import CartItemSummary from './CartItemSummary'
import ProductsRequiringLabelMessage from './ProductsRequiringLabelMessage'

const StyledHeader = styled.header`
  display: flex;
  flex-wrap: wrap;
  min-height: 45px;
  justify-content: space-between;
  width: 100%;
`

const StyledLink = styled(Link)`
  color: ${({ theme }) => theme.colors.component.textDark};
`

const StyledCartItemsWrapper = styled.div`
  width: 100%;
`

export interface CartItemsSummaryProps {
  cart: CartFragment
  className?: string
  orderConfirmed?: boolean
  suppressHeader?: boolean
}

const CartItems: React.FC<Pick<CartItemsSummaryProps, 'cart'>> = React.memo(({ cart }) => {
  const { featureFlags } = useSiteMetadata() ?? {}
  const shippingAddress = cart.shipping_addresses[0]

  // if label status (PL) feature flag enabled, pair items with design fee items
  if (featureFlags?.productLabelStatus) {
    const elements: React.ReactElement[] = []
    for (const pair of collectItemAndDesignFeePairs(cart.items ?? []).values()) {
      elements.push(
        <CartItemSummary
          item={pair.item}
          labelDesignFeeItem={pair.designFeeItem}
          key={pair.item.id}
          shippingAddress={shippingAddress}
        />,
      )
    }
    return <>{elements}</>
  }

  const elements = (cart.items ?? []).reduce((acc: React.ReactElement[], item) => {
    if (!item) return acc
    acc.push(
      <CartItemSummary
        item={item}
        // TODO Refactor the naming of this prop duo.
        key={item.id}
        shippingAddress={shippingAddress}
      />,
    )
    return acc
  }, [])
  return <>{elements}</>
})

CartItems.displayName = 'CartItems'

// TODO Break out a pure CartItemsSummary with just the list of items then
//    when needed render it inside something like CartItemsSummaryWithHeader
//    to cut down on weird props and styling overrides. As part of this, refactor
//    how the borderRadius rules are used as they are handled in a complex
//    way right now.
const CartItemsSummary: React.FC<CartItemsSummaryProps> = ({
  cart,
  className,
  orderConfirmed = false,
  suppressHeader = false,
}) => {
  const { featureFlags } = useSiteMetadata() ?? {}
  return (
    <Grid alignItems="center" className={className} container item justifyContent="space-between">
      {!suppressHeader && (
        <StyledHeader>
          <Grid item>
            <Typography variant="h5" display="inline">
              {orderConfirmed ? 'YOUR ORDER:' : 'YOUR CART:'}
            </Typography>{' '}
            {totalItemsDisplay(totalQuantityExcludingDependents(cart.items, cart.total_quantity))}
          </Grid>
          {!orderConfirmed && (
            <Grid item>
              <StyledLink to="/checkout">Edit Cart</StyledLink>
            </Grid>
          )}
          <CaliforniaProp65Warning
            items={cart.items}
            shippingAddress={cart.shipping_addresses?.[0]}
          />
          {featureFlags?.productLabelStatus && <ProductsRequiringLabelMessage />}
        </StyledHeader>
      )}
      <StyledCartItemsWrapper>
        <CartItems cart={cart} />
      </StyledCartItemsWrapper>
    </Grid>
  )
}

const StyledCartItemsSummary = styled(CartItemsSummary)`
  font-size: 14px;
  max-width: 420px;
`

export default StyledCartItemsSummary
